import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { TabsContent } from '../ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import CidSearch from '../CidSearch';
import { Users, Stethoscope, MessageSquare } from 'lucide-react';
import {
  formatDate,
  displayCPF,
  STATUS_LABELS,
  STATUS_COLORS,
  ORIGEM_LABELS,
  ORIGEM_GRUPOS,
  HGR_SUBGRUPOS,
  formatOrigem,
  getOrigemColor,
  ALTA_MOTIVO_LABELS
} from '../../lib/utils';

export const TabInternacao = ({
  isEditing,
  canEditPatient,
  canViewFullCPF,
  paciente,
  internacao,
  editPaciente,
  setEditPaciente,
  editInternacao,
  setEditInternacao,
  medicos,
  historicoEquipe,
  historicoMedico,
  api
}) => {
  const [medicosSolicitantes, setMedicosSolicitantes] = useState([]);
  const [profissionaisEsp, setProfissionaisEsp] = useState({}); // { fisio: [...users], fono: [...], ... }

  const origemGrupo = isEditing ? editInternacao?.origem_grupo : internacao?.origem_grupo;

  // Map especialidade key to user perfil
  const ESP_PERFIL_MAP = {
    fisio: 'fisioterapeuta',
    fono: 'fonoaudiologo',
    nutri: 'nutricionista',
    psicologo: 'psicologo',
    clinico: 'medico',
    enfermeiro: 'enfermeiro',
  };

  const fetchSolicitantes = useCallback(async () => {
    if (!origemGrupo || !api) { setMedicosSolicitantes([]); return; }
    try {
      const res = await api.get(`/medicos?ativo=true&unidade_origem=${origemGrupo}`);
      setMedicosSolicitantes(res.data || []);
    } catch { setMedicosSolicitantes([]); }
  }, [origemGrupo, api]);

  // Fetch professionals for selected specialties
  const especialidades = isEditing ? (editInternacao?.especialidades || []) : (internacao?.especialidades || []);

  const fetchProfissionais = useCallback(async () => {
    if (!api) return;
    const newMap = {};
    for (const esp of especialidades) {
      const perfil = ESP_PERFIL_MAP[esp];
      if (!perfil) continue;
      try {
        const res = await api.get(`/usuarios?perfil=${perfil}`);
        newMap[esp] = (res.data || []).filter(u => u.ativo !== false);
      } catch { newMap[esp] = []; }
    }
    setProfissionaisEsp(newMap);
  }, [api, especialidades.join(',')]);

  useEffect(() => { fetchSolicitantes(); }, [fetchSolicitantes]);
  useEffect(() => { fetchProfissionais(); }, [fetchProfissionais]);

  return (
  <TabsContent value="internacao">
    <Card>
      <CardHeader>
        <CardTitle>Dados da Internação</CardTitle>
        {isEditing && !canEditPatient && (
          <CardDescription className="text-amber-600">
            Modo de edição ativo. Nome e CPF não podem ser alterados.
          </CardDescription>
        )}
        {isEditing && canEditPatient && (
          <CardDescription className="text-amber-600">
            Modo de edição ativo. Alterações em Nome/CPF serão registradas em auditoria.
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Patient Data Section */}
        <div>
          <h4 className="font-semibold text-sm text-muted-foreground mb-4">Dados do Paciente</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-muted-foreground">Nome</Label>
              {isEditing && canEditPatient ? (
                <Input 
                  value={editPaciente?.nome || ''} 
                  onChange={(e) => setEditPaciente(prev => ({ ...prev, nome: e.target.value }))}
                  className="mt-1"
                  data-testid="input-paciente-nome"
                />
              ) : (
                <Input 
                  value={paciente?.nome || ''} 
                  disabled 
                  className="bg-muted mt-1" 
                />
              )}
            </div>
            <div>
              <Label className="text-muted-foreground">CPF</Label>
              <Input 
                value={displayCPF(paciente?.cpf, canViewFullCPF)} 
                disabled 
                className="bg-muted mt-1" 
                data-testid="perfil-cpf-field"
              />
            </div>
            <div>
              <Label className="text-muted-foreground">Data de Nascimento</Label>
              {isEditing ? (
                <Input 
                  type="date"
                  value={editPaciente?.data_nascimento || ''} 
                  onChange={(e) => setEditPaciente({...editPaciente, data_nascimento: e.target.value})}
                  className="mt-1"
                />
              ) : (
                <p className="font-medium mt-1">{formatDate(paciente?.data_nascimento)}</p>
              )}
            </div>
            <div>
              <Label className="text-muted-foreground">Telefone</Label>
              {isEditing ? (
                <Input 
                  value={editPaciente?.telefone || ''} 
                  onChange={(e) => setEditPaciente({...editPaciente, telefone: e.target.value})}
                  className="mt-1"
                />
              ) : (
                <p className="font-medium mt-1">{paciente?.telefone || '-'}</p>
              )}
            </div>
            <div className="md:col-span-2">
              <Label className="text-muted-foreground">Endereço</Label>
              {isEditing ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-1">
                  <Input 
                    placeholder="Logradouro"
                    value={editPaciente?.logradouro || ''} 
                    onChange={(e) => setEditPaciente({...editPaciente, logradouro: e.target.value})}
                  />
                  <Input 
                    placeholder="Número"
                    value={editPaciente?.numero || ''} 
                    onChange={(e) => setEditPaciente({...editPaciente, numero: e.target.value})}
                  />
                  <Input 
                    placeholder="Bairro"
                    value={editPaciente?.bairro || ''} 
                    onChange={(e) => setEditPaciente({...editPaciente, bairro: e.target.value})}
                  />
                  <Input 
                    placeholder="Cidade"
                    value={editPaciente?.cidade || ''} 
                    onChange={(e) => setEditPaciente({...editPaciente, cidade: e.target.value})}
                  />
                  <Input 
                    placeholder="UF"
                    value={editPaciente?.uf || ''} 
                    onChange={(e) => setEditPaciente({...editPaciente, uf: e.target.value})}
                    maxLength={2}
                  />
                  <Input 
                    placeholder="CEP"
                    value={editPaciente?.cep || ''} 
                    onChange={(e) => setEditPaciente({...editPaciente, cep: e.target.value})}
                  />
                </div>
              ) : (
                <p className="font-medium mt-1">
                  {paciente?.logradouro ? 
                    `${paciente.logradouro}${paciente.numero ? `, ${paciente.numero}` : ''} - ${paciente.bairro || ''}, ${paciente.cidade || ''} / ${paciente.uf || ''}` 
                    : '-'}
                </p>
              )}
            </div>
          </div>
        </div>

        <Separator />

        {/* Admission Data Section */}
        <div>
          <h4 className="font-semibold text-sm text-muted-foreground mb-4">Dados da Internação</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-muted-foreground">Diagnóstico Principal</Label>
              {isEditing ? (
                <Input 
                  value={editInternacao?.diagnostico_principal || ''} 
                  onChange={(e) => setEditInternacao({...editInternacao, diagnostico_principal: e.target.value})}
                  className="mt-1"
                />
              ) : (
                <p className="font-medium mt-1">{internacao?.diagnostico_principal}</p>
              )}
            </div>
            {/* Especialidades */}
            <div className="md:col-span-2">
              <Label className="text-muted-foreground">Especialidades</Label>
              {isEditing ? (
                <div className="space-y-3 mt-1">
                  <div className="flex flex-wrap gap-2">
                    {[
                      { key: 'fisio', label: 'Fisioterapia', color: 'border-green-400 bg-green-50 text-green-700' },
                      { key: 'fono', label: 'Fonoaudiologia', color: 'border-purple-400 bg-purple-50 text-purple-700' },
                      { key: 'nutri', label: 'Nutrição', color: 'border-orange-400 bg-orange-50 text-orange-700' },
                      { key: 'psicologo', label: 'Psicologia', color: 'border-blue-400 bg-blue-50 text-blue-700' },
                      { key: 'clinico', label: 'Clínico', color: 'border-rose-400 bg-rose-50 text-rose-700' },
                      { key: 'enfermeiro', label: 'Enfermeiro(a)', color: 'border-teal-400 bg-teal-50 text-teal-700' },
                    ].map(esp => {
                      const selected = (editInternacao?.especialidades || []).includes(esp.key);
                      return (
                        <button key={esp.key} type="button"
                          onClick={() => {
                            const newEsp = selected
                              ? (editInternacao?.especialidades || []).filter(e => e !== esp.key)
                              : [...(editInternacao?.especialidades || []), esp.key];
                            const newProf = { ...(editInternacao?.profissionais_especialidades || {}) };
                            if (selected) delete newProf[esp.key];
                            setEditInternacao({ ...editInternacao, especialidades: newEsp, profissionais_especialidades: newProf });
                          }}
                          className={`px-3 py-1.5 rounded-full border text-sm font-medium transition-all ${
                            selected ? esp.color : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'
                          }`}
                          data-testid={`edit-tag-esp-${esp.key}`}
                        >
                          {selected && '✓ '}{esp.label}
                        </button>
                      );
                    })}
                  </div>
                  {/* Professional selectors for active specialties */}
                  {(editInternacao?.especialidades || []).length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-muted/30 rounded-lg">
                      {[
                        { key: 'fisio', label: 'Fisioterapeuta', color: 'text-green-700' },
                        { key: 'fono', label: 'Fonoaudiólogo', color: 'text-purple-700' },
                        { key: 'nutri', label: 'Nutricionista', color: 'text-orange-700' },
                        { key: 'psicologo', label: 'Psicólogo', color: 'text-blue-700' },
                        { key: 'clinico', label: 'Clínico', color: 'text-rose-700' },
                        { key: 'enfermeiro', label: 'Enfermeiro(a)', color: 'text-teal-700' },
                      ].filter(esp => (editInternacao?.especialidades || []).includes(esp.key)).map(esp => (
                        <div key={esp.key} className="space-y-1">
                          <Label className={`text-xs font-medium ${esp.color}`}>Profissional {esp.label}</Label>
                          <Select
                            value={(editInternacao?.profissionais_especialidades || {})[esp.key] || ''}
                            onValueChange={(v) => setEditInternacao({
                              ...editInternacao,
                              profissionais_especialidades: {
                                ...(editInternacao?.profissionais_especialidades || {}),
                                [esp.key]: v === '_none' ? '' : v
                              }
                            })}
                          >
                            <SelectTrigger className="h-9" data-testid={`select-prof-${esp.key}`}>
                              <SelectValue placeholder="Selecione o profissional" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_none">Nenhum</SelectItem>
                              {(profissionaisEsp[esp.key] || []).map(u => (
                                <SelectItem key={u.id} value={u.id}>{u.nome}</SelectItem>
                              ))}
                              {(profissionaisEsp[esp.key] || []).length === 0 && (
                                <SelectItem value="_empty" disabled>Nenhum {esp.label.toLowerCase()} cadastrado</SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2 mt-1">
                  <div className="flex flex-wrap gap-1">
                    {(internacao?.especialidades || []).length > 0 ? (
                      internacao.especialidades.map(e => {
                        const labels = { fisio: 'Fisioterapia', fono: 'Fonoaudiologia', nutri: 'Nutrição', psicologo: 'Psicologia', clinico: 'Clínico', enfermeiro: 'Enfermeiro(a)' };
                        const colors = { fisio: 'border-green-300 text-green-700', fono: 'border-purple-300 text-purple-700', nutri: 'border-orange-300 text-orange-700', psicologo: 'border-blue-300 text-blue-700', clinico: 'border-rose-300 text-rose-700', enfermeiro: 'border-teal-300 text-teal-700' };
                        const profId = (internacao?.profissionais_especialidades || {})[e];
                        const profNome = profId ? (profissionaisEsp[e] || []).find(u => u.id === profId)?.nome : null;
                        return (
                          <Badge key={e} variant="outline" className={`text-xs ${colors[e] || ''}`}>
                            {labels[e] || e}{profNome ? ` — ${profNome}` : ''}
                          </Badge>
                        );
                      })
                    ) : (
                      <p className="text-muted-foreground">-</p>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div>
              <Label className="text-muted-foreground">CID-10</Label>
              {isEditing ? (
                <div className="mt-1">
                  <CidSearch
                    value={editInternacao?.cid || ''}
                    onChange={(code) => setEditInternacao({...editInternacao, cid: code})}
                    placeholder="Pesquisar CID-10..."
                    api={api}
                  />
                </div>
              ) : (
                <p className="font-medium mt-1 font-mono">{internacao?.cid10 || internacao?.cid || '-'}</p>
              )}
            </div>
            <div>
              <Label className="text-muted-foreground">Grupo de Origem</Label>
              {isEditing ? (
                <>
                  <Select 
                    value={editInternacao?.origem_grupo || ''} 
                    onValueChange={(v) => setEditInternacao({...editInternacao, origem_grupo: v, origem_subgrupo: v === 'HGR' ? editInternacao?.origem_subgrupo : '', medico_solicitante_id: ''})}
                  >
                    <SelectTrigger className="mt-1" data-testid="edit-select-origem-grupo">
                      <SelectValue placeholder="Selecione o grupo" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ORIGEM_GRUPOS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {editInternacao?.origem_grupo === 'HGR' && (
                    <Select 
                      value={editInternacao?.origem_subgrupo || ''} 
                      onValueChange={(v) => setEditInternacao({...editInternacao, origem_subgrupo: v})}
                    >
                      <SelectTrigger className="mt-1" data-testid="edit-select-origem-subgrupo">
                        <SelectValue placeholder="Selecione o subgrupo" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(HGR_SUBGRUPOS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </>
              ) : (
                <p className="font-medium mt-1">
                  {internacao?.origem_grupo 
                    ? formatOrigem(internacao.origem_grupo, internacao.origem_subgrupo) 
                    : (ORIGEM_LABELS[internacao?.origem] || internacao?.origem || '-')}
                </p>
              )}
            </div>
            {/* Médico solicitante - filtered by unidade_origem */}
            <div>
              <Label className="text-muted-foreground">Médico solicitante {origemGrupo ? `(${ORIGEM_GRUPOS[origemGrupo] || origemGrupo})` : ''}</Label>
              {isEditing ? (
                <Select
                  value={editInternacao?.medico_solicitante_id || ''}
                  onValueChange={(v) => setEditInternacao({...editInternacao, medico_solicitante_id: v})}
                >
                  <SelectTrigger className="mt-1" data-testid="edit-select-medico-solicitante">
                    <SelectValue placeholder={origemGrupo ? 'Selecione o médico' : 'Selecione uma origem primeiro'} />
                  </SelectTrigger>
                  <SelectContent>
                    {medicosSolicitantes.length > 0 ? (
                      medicosSolicitantes.map((m) => (
                        <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>
                      ))
                    ) : (
                      <SelectItem value="_none" disabled>
                        {origemGrupo ? `Nenhum médico para ${ORIGEM_GRUPOS[origemGrupo] || origemGrupo}` : 'Selecione a origem'}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              ) : (
                <p className="font-medium mt-1">
                  {internacao?.medico_solicitante_id
                    ? (medicosSolicitantes.find(m => m.id === internacao.medico_solicitante_id)?.nome || 'Carregando...')
                    : '-'}
                </p>
              )}
            </div>
            <div>
              <Label className="text-muted-foreground">Status</Label>
              {isEditing ? (
                <Select 
                  value={editInternacao?.status_kanban || ''} 
                  onValueChange={(v) => setEditInternacao({...editInternacao, status_kanban: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Badge className={`mt-1 ${STATUS_COLORS[internacao?.status_kanban]}`}>
                  {STATUS_LABELS[internacao?.status_kanban]}
                </Badge>
              )}
            </div>
            <div>
              <Label className="text-muted-foreground">Data Internação</Label>
              {isEditing ? (
                <Input 
                  type="date"
                  value={editInternacao?.data_internacao || ''} 
                  onChange={(e) => setEditInternacao({...editInternacao, data_internacao: e.target.value})}
                  className="mt-1"
                />
              ) : (
                <p className="font-medium mt-1">{formatDate(internacao?.data_internacao)}</p>
              )}
            </div>
            <div>
              <Label className="text-muted-foreground">Previsão de Alta</Label>
              {isEditing ? (
                <Input 
                  type="date"
                  value={editInternacao?.previsao_alta || ''} 
                  onChange={(e) => setEditInternacao({...editInternacao, previsao_alta: e.target.value})}
                  className="mt-1"
                />
              ) : (
                <p className="font-medium mt-1">{formatDate(internacao?.previsao_alta)}</p>
              )}
            </div>
            <div>
              <Label className="text-muted-foreground">Médico(a) responsável</Label>
              {isEditing ? (
                <Select 
                  value={editInternacao?.medico_id || editInternacao?.medico_responsavel_id || ''} 
                  onValueChange={(v) => setEditInternacao({...editInternacao, medico_id: v})}
                >
                  <SelectTrigger className="mt-1" data-testid="select-medico-edit">
                    <SelectValue placeholder="Selecione o médico(a)" />
                  </SelectTrigger>
                  <SelectContent>
                    {medicos.filter(m => m.ativo !== false).map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="font-medium mt-1">{internacao?.medico_nome || 'Não definido'}</p>
              )}
            </div>
            <div className="md:col-span-2">
              <Label className="text-muted-foreground">Observações</Label>
              {isEditing ? (
                <Textarea 
                  value={editInternacao?.observacoes || ''} 
                  onChange={(e) => setEditInternacao({...editInternacao, observacoes: e.target.value})}
                  className="mt-1"
                  rows={3}
                />
              ) : (
                <p className="font-medium mt-1">{internacao?.observacoes || '-'}</p>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Alta Administrativa Comment */}
    {internacao?.comentario_alta_administrativa && (
      <Card className="mt-4 border-amber-300 dark:border-amber-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 text-amber-700 dark:text-amber-400">
            <MessageSquare className="h-4 w-4" />
            Justificativa da alta administrativa
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm" data-testid="comentario-alta-administrativa-display">{internacao.comentario_alta_administrativa}</p>
          <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
            {internacao.alta_administrativa_usuario && (
              <span>Registrado por: {internacao.alta_administrativa_usuario}</span>
            )}
            {internacao.alta_administrativa_timestamp && (
              <span>{formatDate(internacao.alta_administrativa_timestamp)}</span>
            )}
          </div>
        </CardContent>
      </Card>
    )}

    {/* Histórico de Equipes */}
    <Card className="mt-4">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Users className="h-4 w-4" />
          Histórico de equipes
        </CardTitle>
        <CardDescription>Registro de mudanças de equipe ao longo da internação</CardDescription>
      </CardHeader>
      <CardContent>
        {historicoEquipe.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">Nenhum registro de equipe</p>
        ) : (
          <div className="border rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-2 font-medium">Equipe</th>
                  <th className="text-left p-2 font-medium">Data início</th>
                  <th className="text-left p-2 font-medium">Data fim</th>
                  <th className="text-left p-2 font-medium">Alterado por</th>
                  <th className="text-left p-2 font-medium">Motivo</th>
                </tr>
              </thead>
              <tbody>
                {historicoEquipe.map((h, idx) => (
                  <tr key={h.id} className={`border-t ${idx === 0 && !h.fim_em ? 'bg-green-50 dark:bg-green-900/10' : ''}`} data-testid={`hist-equipe-row-${h.id}`}>
                    <td className="p-2 font-medium">
                      {h.equipe_nome || 'Sem equipe'}
                      {idx === 0 && !h.fim_em && <Badge className="ml-2 bg-green-100 text-green-800 text-xs">Atual</Badge>}
                    </td>
                    <td className="p-2 text-muted-foreground">{h.inicio_em ? new Date(h.inicio_em).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '-'}</td>
                    <td className="p-2 text-muted-foreground">{h.fim_em ? new Date(h.fim_em).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '-'}</td>
                    <td className="p-2">{h.alterado_por_nome || '-'}</td>
                    <td className="p-2 text-muted-foreground">{h.motivo || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>

    {/* Histórico de Médicos */}
    <Card className="mt-4">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Stethoscope className="h-4 w-4" />
          Histórico de médicos
        </CardTitle>
        <CardDescription>Registro de mudanças de médico ao longo da internação</CardDescription>
      </CardHeader>
      <CardContent>
        {historicoMedico.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">Nenhum registro de médico</p>
        ) : (
          <div className="border rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-2 font-medium">Médico(a)</th>
                  <th className="text-left p-2 font-medium">Data início</th>
                  <th className="text-left p-2 font-medium">Data fim</th>
                  <th className="text-left p-2 font-medium">Alterado por</th>
                  <th className="text-left p-2 font-medium">Motivo</th>
                </tr>
              </thead>
              <tbody>
                {historicoMedico.map((h, idx) => (
                  <tr key={h.id} className={`border-t ${idx === 0 && !h.fim_em ? 'bg-blue-50 dark:bg-blue-900/10' : ''}`} data-testid={`hist-medico-row-${h.id}`}>
                    <td className="p-2 font-medium">
                      {h.medico_nome || 'Sem médico'}
                      {idx === 0 && !h.fim_em && <Badge className="ml-2 bg-blue-100 text-blue-800 text-xs">Atual</Badge>}
                    </td>
                    <td className="p-2 text-muted-foreground">{h.inicio_em ? new Date(h.inicio_em).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '-'}</td>
                    <td className="p-2 text-muted-foreground">{h.fim_em ? new Date(h.fim_em).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '-'}</td>
                    <td className="p-2">{h.alterado_por_nome || '-'}</td>
                    <td className="p-2 text-muted-foreground">{h.motivo || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  </TabsContent>
  );
};
